import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FaTimes } from "react-icons/fa"; // Imported X icon
// Removed OButton import since we no longer use the footer button

const InterviewTypeModal = ({
  isOpen,
  onClose,
  onSelectAI,
  onSelectZoom,
  employeeName,
  isReschedule = false,
  isLoading = false,
  currentInterviewType = null,
  currentItem = null,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleCopy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  /* Normalize the current interview type */
  const normalizedInterviewType =
    String(currentInterviewType || "")
      .trim()
      .toLowerCase();
  const isCurrentAI =
    normalizedInterviewType === "ai" ||
    normalizedInterviewType === "ai interview";
  const isCurrentZoom =
    normalizedInterviewType === "zoom" ||
    normalizedInterviewType === "zoom interview";

  /* Determine the action text for each interview type */
  const aiButtonLabel =
    isReschedule && isCurrentAI
      ? "Reschedule AI Interview"
      : isReschedule && isCurrentZoom
        ? "Schedule AI Interview"
        : "AI Interview";
  const zoomButtonLabel =
    isReschedule && isCurrentZoom
      ? "Reschedule Zoom Interview"
      : isReschedule && isCurrentAI
        ? "Schedule Zoom Interview"
        : "Zoom Interview";

  const title = isReschedule ? "Reschedule Interview" : "Select Interview Type";
  const message = `How would you like to ${isReschedule ? "re-schedule" : "conduct"} the interview${employeeName ? ` for ${employeeName}` : ""}?`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 opacity-75"
            onClick={isLoading ? undefined : onClose}
          />
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative">
          
          {/* Cross / Close Button */}
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="absolute top-4 right-4 z-10 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition focus:outline-none disabled:opacity-50"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>

          <div className="bg-white px-6 pt-6 pb-6 sm:p-6">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                {/* Title */}
                <h3 className="text-lg leading-6 font-semibold text-gray-900 pr-8">
                  {title}
                </h3>
                {/* Message */}
                <div className="mt-2">
                  <p className="text-sm text-gray-500"> {message} </p>
                </div>

                {/* Current Meeting Details Block */}
                {isReschedule && currentItem && (
                  <div className="mt-5 mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="font-semibold mb-3 text-sm text-gray-700">
                      Current Interview Details
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="text-sm">
                        <span className="text-gray-500 block mb-1">Date & Time</span>
                        <span className="font-medium text-gray-900">
                          {currentItem?.interviewDate
                            ? dayjs(currentItem.interviewDate).format("MM-DD-YYYY hh:mm A")
                            : "Not specified"}
                        </span>
                      </div>

                      <div className="text-sm">
                        <span className="text-gray-500 block mb-1">Type</span>
                        <span className="font-medium text-gray-900 capitalize">
                          {currentItem?.interviewType || "N/A"}
                        </span>
                      </div>

                      {currentItem?.interviewType === "zoom" &&
                        currentItem?.interviewMeetingData && (
                          <>
                            <div className="text-sm">
                              <span className="text-gray-500 block mb-1">Meeting ID</span>
                              <span className="font-medium text-gray-900">
                                {currentItem.interviewMeetingData.id || "N/A"}
                              </span>
                            </div>
                            <div className="text-sm">
                              <span className="text-gray-500 block mb-1">Password</span>
                              <span className="font-medium text-gray-900">
                                {currentItem.interviewMeetingData.password || "N/A"}
                              </span>
                            </div>
                            <div className="col-span-1 sm:col-span-2 text-sm">
                              <span className="text-gray-500 block mb-1">Start URL</span>
                              <div className="flex items-center gap-2">
                                <a
                                  href={currentItem.interviewMeetingData.start_url || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 truncate hover:underline"
                                >
                                  {currentItem.interviewMeetingData.start_url || "N/A"}
                                </a>
                                {currentItem.interviewMeetingData.start_url && (
                                  <button
                                    onClick={() =>
                                      handleCopy(currentItem.interviewMeetingData.start_url)
                                    }
                                    className="text-xs bg-white border border-gray-300 px-2 py-1 rounded hover:bg-gray-100 text-gray-700"
                                  >
                                    Copy
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                      {currentItem?.interviewType === "ai" && (
                        <div className="col-span-1 sm:col-span-2 text-sm">
                          <span className="text-gray-500 block mb-1">Session ID</span>
                          <span className="font-medium text-gray-900">
                            {currentItem.aiInterviewSessionID || "N/A"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Interview Type Options */}
                <div className="mt-4 flex flex-col gap-3">
                  {/* AI Interview */}
                  <button
                    type="button"
                    onClick={onSelectAI}
                    disabled={isLoading}
                    className="flex items-start gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-green-500 hover:bg-green-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-xl"> 🤖 </span>
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        {aiButtonLabel}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {isCurrentZoom && isReschedule
                          ? "Start a new automated interview with AI"
                          : "Automated interview with AI"}
                      </span>
                    </span>
                  </button>
                  {/* Zoom Interview */}
                  <button
                    type="button"
                    onClick={onSelectZoom}
                    disabled={isLoading}
                    className="flex items-start gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-blue-500 hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-xl"> 🎥 </span>
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        {zoomButtonLabel}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {isCurrentAI && isReschedule
                          ? "Schedule a new live Zoom interview"
                          : "Schedule a live Zoom interview"}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewTypeModal;